/**
 * WHAT A READER IS TOLD ABOUT A PASSAGE.
 *
 * Whose translation this is and on what terms, where to go and check it against somebody
 * else, how far the passage around it runs, and which names inside it lead somewhere. None
 * of that is markup — it is the answer the markup then dresses. It lived inside 846 lines
 * of HTML strings in the browser, which is a place a phone app cannot reach and a place no
 * test can hold anything still.
 *
 * So the model is here and the markup stays where it is drawn. Same rule as map.ts: what to
 * say is shared, how it looks belongs to whoever is doing the looking.
 *
 * The derivations come from @versesverse/schema, which is where facts derived rather than
 * stored are worked out — importing them rather than re-deriving them is the whole point of
 * that package, and this file is the first thing in core to lean on it in a browser.
 */
import { bookKey, passageUnitOf } from '@versesverse/schema/derive';

export { bookKey, passageUnitOf };

/* ---------------------------------------------------------------- reading it elsewhere */

/** Somewhere a passage can be read that is not this page. */
export interface Reader {
  name: string;
  /**
   * Where to send a reader, or **null where this site does not carry the passage**.
   *
   * Null was added 2026-08-18 for a case a fallback cannot answer. Sefaria has no Baruch at all, so
   * every one of its 213 verses linked to a 404 — and this file's own rule is that a text with
   * nowhere to go gets NO link rather than a wrong one. A fallback to a front page is the right
   * answer when a reference will not parse; it is the wrong answer when the site simply lacks the
   * book, because the reader is then sent somewhere real to look for something that is not there.
   */
  url: (ref: string) => string | null;
}

/**
 * WHERE TO GO AND DISAGREE.
 *
 * Every passage offers a link out, which is the whole basis of "read it yourself" — and the
 * link used to be chosen by a rule that sent anything not Quranic to BibleGateway. That was
 * true while there were three texts. When the Mishnah arrived, all 4,192 of its passages
 * offered to open a Mishnah reference in the King James Bible, on a site that does not
 * carry the Mishnah.
 *
 * So a text states where it can be read, and a text that states nothing gets no link rather
 * than a wrong one. The rule is in schema/corpora.ts as `readOnline`; this is what each of
 * those names points at.
 */
/*
 * BOOKS SEFARIA NAMES DIFFERENTLY, AND BOOKS IT DOES NOT HAVE — measured 2026-08-18.
 *
 * The Sefaria reader transformed a reference blindly: spaces to underscores, colon to dot. That is
 * right for every book whose name this library and Sefaria happen to share, and **1,700 of the
 * 4,374 deuterocanonical verses — 39% — did not.** Every one linked to a 404.
 *
 * Tested book by book against the live site rather than reasoned about:
 *
 *   Tobit · Judith · Wisdom · Sirach     200, and the blind transform was right all along
 *   I Maccabees · II Maccabees           404 here, 200 as "The Book of Maccabees I" / "II"
 *   Baruch                               404 under every name tried — Sefaria has no Baruch
 *
 * So the two Maccabees are a renaming, 1,487 verses recovered. **Baruch is not**, and its 213
 * verses now get no link at all, which is this file's own rule: a text with nowhere to go gets no
 * link rather than a wrong one. A 404 is worse than both — it looks like the library is broken
 * rather than like Sefaria is missing a book.
 */
export const SEFARIA_TITLES = new Map<string, string>([
  ['I Maccabees', 'The Book of Maccabees I'],
  ['II Maccabees', 'The Book of Maccabees II'],
]);

/** Books this library carries that Sefaria does not have under any name tried. */
export const SEFARIA_ABSENT = new Set<string>(['Baruch']);

export const READERS: Record<string, Reader> = {
  biblegateway: {
    name: 'BibleGateway',
    /* Named translation: a reader who follows the link must land on the words this page
       quoted, not on whichever version the site happens to default to. */
    url: ref => 'https://www.biblegateway.com/passage/?search=' + encodeURIComponent(ref) + '&version=KJV',
  },
  quran: {
    name: 'quran.com',
    url: ref => {
      const m = /Quran\s+(\d+):(\d+)/.exec(String(ref ?? ''));
      /* A reference that will not parse still goes somewhere real rather than to a 404. */
      return m ? `https://quran.com/${m[1]}/${m[2]}` : 'https://quran.com';
    },
  },
  /*
   * THE HADITH WENT TO QURAN.COM UNTIL 2026-08-18, WHICH IS THE MISHNAH DEFECT AGAIN.
   *
   * `HADITH` declared `readOnline: 'quran'`, and a hadith reference is `Hadith 1:3377`, which the
   * Quran pattern above cannot match. So the fallback fired and **all 2,328 hadith offered the
   * quran.com HOMEPAGE as the place to read them** — on a site that does not carry hadith at all.
   * Exactly what this file's header describes happening to the Mishnah and BibleGateway, in a
   * second place, and it survived two days after being written down in a note nobody was obliged
   * to read.
   *
   * The right target existed the whole time. These passages come from HadeethEnc, the reference's
   * second component IS HadeethEnc's own hadith id, and the URL resolves — `Hadith 1:3377` ->
   * `hadeethenc.com/en/browse/hadith/3377`, checked live and returning 200.
   *
   * So this is not "remove a wrong link". A reader following it now lands on the very page this
   * text was taken from, which is what the link was always claiming to offer.
   */
  hadeethenc: {
    name: 'HadeethEnc',
    url: ref => {
      const m = /Hadith\s+\d+:(\d+)/.exec(String(ref ?? ''));
      /* Same rule as the Quran reader: a reference that will not parse still goes somewhere real
         rather than to a 404 — but here the fallback is a site that actually carries the text. */
      return m ? `https://hadeethenc.com/en/browse/hadith/${m[1]}` : 'https://hadeethenc.com/en/browse';
    },
  },
  sefaria: {
    /* Spaces become underscores and the chapter separator a dot, so Mishnah Berakhot 1:1
       is Mishnah_Berakhot_1.1 and the folio Berakhot 2a:1 is Berakhot_2a.1. Sefaria's own
       links put a dot before the chapter as well — Mishnah_Berakhot.1.1 — and its
       reference parser resolves both to the same passage; verified against the live site,
       and pinned in the tests so the shape is not tidied without re-checking. */
    name: 'Sefaria',
    url: ref => {
      const raw = String(ref ?? '').trim();
      const book = raw.replace(/\s+\d+[:.]\d+.*$/, '');
      if (SEFARIA_ABSENT.has(book)) return null;
      const titled = SEFARIA_TITLES.get(book);
      const addressed = titled ? raw.replace(book, titled) : raw;
      return 'https://www.sefaria.org/' + encodeURIComponent(
        addressed.replace(/\s+/g, '_').replace(/:/g, '.')).replace(/%2F/g, '/');
    },
  },
};

/** The site a text names, or null — never a guess. */
export function readerFor(readOnline: string | null | undefined): Reader | null {
  return (readOnline && READERS[readOnline]) || null;
}

/** Where this passage can be read elsewhere, or null if its text names nowhere. */
export function sourceUrl(ref: string, readOnline: string | null | undefined): string | null {
  const r = readerFor(readOnline);
  return r ? r.url(ref) : null;
}

/* ------------------------------------------------------------------- whose words these are */

/** A translation, as far as a reader needs to know. */
export interface EditionNote {
  title: string;
  licence: string;
  /** Whether the terms forbid selling it — stated, never inferred from silence. */
  nonCommercial: boolean;
}

/**
 * WHO TRANSLATED THIS, AND ON WHAT TERMS.
 *
 * The edition is chosen per book, on how completely it carries that book, so the library is
 * not one translator's voice throughout — and a reader is entitled to know whose words they
 * are reading rather than infer it from the prose. Where a text carries an ongoing
 * condition that is stated too: the Talmud may be shown publicly and reused with
 * attribution, but not sold.
 *
 * Keyed through schema's `bookKey`, because the reference is the authority and not the
 * stored book field — those two disagree for the Quran, and keying on the field once left
 * every Quranic passage unable to name its own translation.
 */
export function editionFor(
  corpusId: string,
  ref: string,
  bookEditions: Record<string, number[]> | undefined,
  editions: Partial<EditionNote>[] | undefined,
): EditionNote | null {
  return editionsFor(corpusId, ref, bookEditions, editions).english;
}

/**
 * BOTH EDITIONS A PASSAGE IS READ IN — the English, and the original beside it.
 *
 * `editionFor` answers the question a reader asks: whose English am I reading. A server
 * asking permission has to ask about both, because permission is granted per edition and
 * the two can differ: the Talmud's Aramaic and its English are separate grants, and one can
 * arrive without the other. `schema/permissions.ts` already models that — `mayPublish`
 * decides the passage, `mayPublishOriginal` decides only what travels beside it — and it
 * needed a way to be told which editions a verse actually carries.
 *
 * Written here rather than in the server for the reason everything else in this file is
 * here: a second implementation of "which edition is this passage in" is exactly the defect
 * that left every Quranic passage unable to name its own translation. `editionFor` is now
 * this function's first field, so there is one lookup and not two.
 */
export function editionsFor(
  corpusId: string,
  ref: string,
  bookEditions: Record<string, number[]> | undefined,
  editions: Partial<EditionNote>[] | undefined,
): { english: EditionNote | null; original: EditionNote | null } {
  const pick = (bookEditions ?? {})[bookKey(corpusId, ref)];
  if (!pick || !pick.length) return { english: null, original: null };
  const at = (i: number | undefined): EditionNote | null => {
    if (i == null || i < 0) return null;
    const ed = (editions ?? [])[i];
    if (!ed || !ed.title) return null;
    return { title: ed.title, licence: ed.licence ?? '', nonCommercial: !!ed.nonCommercial };
  };
  /* A book with one entry is a text whose original was never recorded separately — most of
     them. `pick[1]` being absent is the ordinary case, not a fault. */
  return { english: at(pick[0]), original: at(pick[1]) };
}

/* --------------------------------------------------------------- how far the passage runs */

export interface ContextRange {
  /** Ids before the verse, in reading order. */
  before: number[];
  /** Ids after it, in reading order. */
  after: number[];
  /** The unit both ends stopped at. */
  unit: string;
}

/**
 * THE PASSAGE AROUND A VERSE.
 *
 * A verse quoted on its own is the thing this project is most often accused of doing to
 * scripture, so any verse opens into the passage it sits in. Verse ids are positions in
 * reading order, so the neighbours are the ids on either side — bounded by the unit the
 * text names, because running past the end of Malachi into Matthew is not context, and
 * neither is running out of Al-Baqarah into Al-Imran.
 *
 * `unitAt` is injected rather than derived here: what unit an id belongs to depends on the
 * bundle's packed corpus encoding, which is the browser's business. See derive.ts's
 * `passageUnitOf` for the rule it should be built from.
 */
export function contextRange(
  id: number,
  span: number,
  unitAt: (id: number) => string,
  count: number,
): ContextRange {
  const unit = unitAt(id);
  const before: number[] = [];
  const after: number[] = [];

  /* Walking backwards and unshifting, so `before` comes out in reading order — a passage
     collected backwards and printed as collected reads bottom to top. */
  for (let i = id - 1; i >= 0 && before.length < span; i--) {
    if (unitAt(i) !== unit) break;
    before.unshift(i);
  }
  /*
   * `break`, not `continue`. A unit that appears twice in the reading order — which
   * splitBooks exists to catch, and which has happened — would otherwise be stitched back
   * together across everything in between, and the passage around a verse would quietly
   * include text from the far side of another book.
   */
  for (let i = id + 1; i < count && after.length < span; i++) {
    if (unitAt(i) !== unit) break;
    after.push(i);
  }
  return { before, after, unit };
}

/* ------------------------------------------------------- which names lead somewhere else */

/*
 * MOVED TO `schema/names.ts` on 2026-08-20, and re-exported here so that nothing importing it from
 * `core` has to change.
 *
 * It went because `pipeline` cannot import `core`, and that one fact meant the static pages could
 * not link a single name in a single verse while the app linked every one of them. A rule both
 * surfaces need belongs in `schema`, which is the layer both already read.
 */
export { nameSpans, nameFormsOf } from '@versesverse/schema/names';
export type { NameForms, NameSpan, NamedFigure } from '@versesverse/schema/names';

/**
 * TRAVELLING BY WORD.
 *
 * The point of a map like this is that you can move through it, and reading a verse should
 * not be a dead end: every name inside the text is a door to that name's own verses. So the
 * English is scanned for names the map knows, and the caller turns each span into a link.
 *
 * Spellings count, not just labels — someone reading "Musa" in the Quran is reading about
 * Moses, and a name-only scan would tell them the scriptures do not mention him.
 *
 * Returned as SPANS rather than as marked-up text, because a phone app does not build the
 * same thing out of them that a web page does, and because a function that returns ranges
 * can be tested and one that returns a string of HTML mostly cannot.
 */
